import { useRef } from 'react';

import OfferFiltersChips from './OfferFiltersChips';

import type { FilterOptionOnClick } from '../types';
import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { OfferFilter } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { ChipItem } from '@shared/ui/common/BpkChipGroup';

import {
  usePriceContext,
  useRoomFilters,
  useHotelOffersActions,
  useRoomTypeFilterWithPrice,
  useHotelOffersContext,
  useRoomFiltersWithDimensions,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import {
  BED_TYPE_KEYS,
  OFFER_OPTION_KEYS,
  ROOM_TYPE_KEYS,
  UBFF_FILTER_KEYS_MAP,
} from '@hotels-hotel-detail/utils/hotel-detail-price';
import {
  getFilterParamsToUpdateUrl,
  getNextRequestFilters,
} from '@hotels-hotel-detail/utils/hotel-offers';
import { buildOfferFiltersMBDAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/offer-filters';
import {
  buildAdditionalInfoBaseRoomTypeFilterSelected,
  buildAdditionalInfoOfferFiltersClick,
} from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import {
  BREAKPOINTS,
  withBpkBreakpoint,
} from '@shared/ui/common/BpkBreakpoint';
import BpkMultiSelectChipGroup, {
  CHIP_GROUP_TYPES,
} from '@shared/ui/common/BpkChipGroup';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import { scrollToHotelPrices } from '@shared/utils-frontend/navigation-service';
import {
  getCurrentQueryParam,
  updateSearchParamsUrl,
} from '@shared/utils-frontend/url/url';
import {
  SEO_TRAFFIC_SOURCE,
  trafficSource,
} from '@shared/utils-universal/traffic-source';

export type OfferFilterProps = {
  isMobile?: boolean;
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
  userContext: UserContext;
  scrollAfterFilter?: boolean;
  openMultiplePrice?: boolean;
  configs: Configs;
};

const getFilteredFilters = ({
  enableFiltersWithProviders,
  enableFiltersWithRoomGroups,
  enableRoomNameDimensions,
  originalFilters,
  roomFiltersWithDimensions,
  roomFiltersWithPrices,
}: {
  originalFilters: OfferFilter[];
  roomFiltersWithDimensions: OfferFilter[];
  enableFiltersWithProviders?: boolean;
  enableFiltersWithRoomGroups?: boolean;
  enableRoomNameDimensions?: boolean;
  roomFiltersWithPrices?: OfferFilter;
}): OfferFilter[] => {
  const filtersWithoutRoomType = originalFilters.filter((item) =>
    OFFER_OPTION_KEYS.includes(item.key),
  );
  const providerFilters = originalFilters.filter(
    (item) => item.key === UBFF_FILTER_KEYS_MAP.OPTION_KEY_PROVIDER,
  );
  const offerFiltersWithProviders = [
    ...filtersWithoutRoomType,
    ...providerFilters,
  ];

  if (enableFiltersWithProviders) return offerFiltersWithProviders;

  if (enableRoomNameDimensions) {
    return roomFiltersWithDimensions
      ? [...filtersWithoutRoomType, ...roomFiltersWithDimensions]
      : filtersWithoutRoomType;
  }

  if (enableFiltersWithRoomGroups) {
    return roomFiltersWithPrices
      ? [...offerFiltersWithProviders, roomFiltersWithPrices]
      : offerFiltersWithProviders;
  }

  return originalFilters.filter((item) => !BED_TYPE_KEYS.includes(item.key));
};

const getChipItems = ({
  filterOptionOnClick,
  filters,
  formatCurrency,
  showRoomPrices,
}: {
  filters: OfferFilter[];
  filterOptionOnClick: FilterOptionOnClick;
  formatCurrency: (value: number, format?: string) => string;
  showRoomPrices: boolean;
}): ChipItem[] =>
  filters.flatMap((filter) => {
    if (filter.key === UBFF_FILTER_KEYS_MAP.OPTION_KEY_PROVIDER) {
      return [];
    }

    return filter.options.map((option) => {
      const price = Number(option.label);
      const isValidPrice = !Number.isNaN(price) && price !== 0;

      const shouldShowPrice =
        ROOM_TYPE_KEYS.includes(filter.key) && showRoomPrices && isValidPrice;

      const text = shouldShowPrice
        ? `${option.displayName} (${formatCurrency(price)}+)`
        : option.displayName;

      return {
        text,
        selected: option.selected,
        onClick: filterOptionOnClick([option], filter.key),
        ...buildOfferFiltersMBDAttributes(option.option),
      };
    });
  });

const OfferFilters = ({
  configs: {
    enableBedTypeFilter,
    enableBedTypeWithPrices,
    enableRoomNameDimensions,
  },
  i18n: { formatCurrency, translate },
  isMobile,
  miniEventTracker,
  openMultiplePrice,
  scrollAfterFilter,
  userContext: { enablePartnersFilter },
}: OfferFilterProps) => {
  const { isSeoTraffic, searchId } = usePriceContext();
  const { applyFilters, applyFiltersWithPriceRanges } = useHotelOffersActions();
  const { state } = useHotelOffersContext();

  const originalFilters = useRoomFilters() ?? [];
  const roomFiltersWithPrices = useRoomTypeFilterWithPrice(enableBedTypeFilter);
  const roomFiltersWithDimensions = useRoomFiltersWithDimensions();
  const partnersFilter = originalFilters.find(
    (item) => item.key === UBFF_FILTER_KEYS_MAP.OPTION_KEY_PROVIDER,
  );
  const filters = getFilteredFilters({
    originalFilters,
    enableFiltersWithProviders: enablePartnersFilter,
    enableFiltersWithRoomGroups: enableBedTypeFilter,
    enableRoomNameDimensions,
    roomFiltersWithPrices,
    roomFiltersWithDimensions,
  });

  const priceRangeFilter = state?.response?.responseFilters?.priceRangeFilter;

  // This cacheRef if to support OfferBottomSheet.tsx. Discussion:
  // https://skyscanner.slack.com/archives/C03Q7616FSM/p1732698377193529?thread_ts=1732698252.639389&cid=C03Q7616FSM
  const cacheRef = useRef<JSX.Element | null>(null);

  const handlePriceRangeChipClick =
    (priceRange: { minPrice?: number; maxPrice?: number }) => () => {
      const currentPriceRanges =
        state?.request?.requestFilters?.priceRangeFilter || [];

      const updatedPriceRanges = currentPriceRanges.filter(
        (range) => range.minPrice !== priceRange.minPrice,
      );

      const nextRequestFilters = getNextRequestFilters({
        prevResponseFilters: filters,
        clickedOptionKeys: [],
      });

      // Update both offer filters and price ranges in the UBFF request
      applyFiltersWithPriceRanges(nextRequestFilters, updatedPriceRanges);

      updateSearchParamsUrl({
        source: isSeoTraffic
          ? SEO_TRAFFIC_SOURCE
          : trafficSource({
              source: getCurrentQueryParam('source'),
            }),
        filters: getFilterParamsToUpdateUrl(nextRequestFilters),
        priceFilters:
          updatedPriceRanges.length > 0
            ? JSON.stringify(updatedPriceRanges)
            : undefined,
        searchCycleId: searchId,
      });

      if (scrollAfterFilter) {
        scrollToHotelPrices();
      }
    };

  const priceRangeChips: ChipItem[] =
    priceRangeFilter?.options
      ?.filter((option) => option.selected)
      .map((option) => ({
        text: option.text,
        selected: true,
        onClick: handlePriceRangeChipClick(option.value),
      })) ?? [];

  const filterOptionOnClick: FilterOptionOnClick =
    (options, filterKey) => () => {
      const nextRequestFilters = getNextRequestFilters({
        prevResponseFilters: filters,
        clickedOptionKeys: options.map((option) => option.option),
      });

      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.DETAILS_RATES_FILTER_CLICK,
        buildAdditionalInfoOfferFiltersClick(
          nextRequestFilters.flatMap((filter) => filter.options),
        ),
      );

      // This is a temporary solution to track room type filter click (we should not have such key mappings)
      // we should move to MBD and remove this when MBD is supported in BPK Chip Group.
      if (ROOM_TYPE_KEYS.includes(filterKey)) {
        options.forEach((option) => {
          miniEventTracker.trackHotelsAction(
            ACTION_TYPE.BASE_ROOM_TYPE_FILTER_CLICKED,
            buildAdditionalInfoBaseRoomTypeFilterSelected(
              option.option,
              !option.selected, // when the original option is selected, it will be deselected by the click and vice versa.
            ),
          );
        });
      }

      applyFilters(nextRequestFilters);
      updateSearchParamsUrl({
        source: isSeoTraffic
          ? SEO_TRAFFIC_SOURCE
          : trafficSource({
              source: getCurrentQueryParam('source'),
            }),
        filters: getFilterParamsToUpdateUrl(nextRequestFilters),
        searchCycleId: searchId,
      });

      if (scrollAfterFilter) {
        scrollToHotelPrices();
      }
    };

  if (enablePartnersFilter) {
    const singleChipFilterWithoutRoomType = filters.filter((item) =>
      OFFER_OPTION_KEYS.includes(item.key),
    );

    const chips = getChipItems({
      filters: singleChipFilterWithoutRoomType,
      filterOptionOnClick,
      formatCurrency,
      showRoomPrices: enableBedTypeWithPrices,
    });

    const allChips = [...chips, ...priceRangeChips];

    return (
      <OfferFiltersChips
        chips={allChips}
        partnersFilter={partnersFilter}
        filterOptionOnClick={filterOptionOnClick}
        openMultiplePrice={openMultiplePrice}
      />
    );
  }

  if (
    !openMultiplePrice &&
    filters.length === 0 &&
    priceRangeChips.length === 0
  ) {
    return null;
  }

  const chipItems = getChipItems({
    filters,
    filterOptionOnClick,
    formatCurrency,
    showRoomPrices: enableBedTypeWithPrices,
  });

  const allChipItems = [...chipItems, ...priceRangeChips];

  const label = translate('Filters_label_filterBy');

  const rateFilters = (
    <BpkMultiSelectChipGroup
      label={isMobile ? undefined : label}
      ariaLabel={label}
      type={CHIP_GROUP_TYPES.rail}
      chips={allChipItems}
      leadingNudgerLabel={translate(
        'Offer_filters_chip_group_leading_nudger_label',
      )}
      trailingNudgerLabel={translate(
        'Offer_filters_chip_group_trailing_nudger_label',
      )}
    />
  );

  if (filters.length > 0 || priceRangeChips.length > 0) {
    cacheRef.current = rateFilters;
  }

  return filters.length > 0 || priceRangeChips.length > 0
    ? rateFilters
    : cacheRef.current;
};

export default withI18n(
  withUserContext(
    withMiniEventTracker(
      withConfiguration(
        withBpkBreakpoint(OfferFilters, BREAKPOINTS.MOBILE, 'isMobile'),
      ),
    ),
  ),
);
